import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Archive, Eye, EyeOff, Sparkles, CalendarDays } from 'lucide-react';
import { investmentsAPI } from '../../lib/api';
import { fmtEur } from './investmentShared';
import { OPP_STATUS, fmtTarget, daysUntil } from './OpportunityCards';

const EMPTY = { name: '', asset: 'BTC', target_date: '', description: '', min_amount: '', status: 'upcoming', published: true };

const OpportunityDialog = ({ open, initial, onClose, onSaved }) => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (open) setForm(initial ? { ...EMPTY, ...initial, min_amount: initial.min_amount ?? '' } : EMPTY); }, [open, initial]);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const save = async () => {
        if (form.name.trim().length < 3) { toast.error('Indica un nombre (mínimo 3 caracteres)'); return; }
        if (!form.target_date) { toast.error('Indica la fecha objetivo'); return; }
        setSaving(true);
        try {
            const payload = { ...form, min_amount: form.min_amount === '' ? null : parseFloat(form.min_amount) };
            if (initial?.id) await investmentsAPI.adminUpdateOpportunity(initial.id, payload);
            else await investmentsAPI.adminCreateOpportunity(payload);
            toast.success(initial?.id ? 'Oportunidad actualizada' : 'Oportunidad publicada');
            onSaved(); onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar la oportunidad');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white" data-testid="opportunity-dialog">
                <DialogHeader><DialogTitle className="text-white">{initial?.id ? 'Editar oportunidad' : 'Nueva oportunidad'}</DialogTitle></DialogHeader>
                <div className="space-y-3 py-1">
                    <div className="space-y-1"><p className="text-slate-400 text-xs">Nombre</p>
                        <Input value={form.name} onChange={set('name')} placeholder="Ej: Fondo BTC Q1 2027" className="bg-slate-950 border-slate-800 text-white" data-testid="opportunity-name-input" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1"><p className="text-slate-400 text-xs">Activo</p>
                            <div className="grid grid-cols-2 gap-1.5">
                                {['BTC', 'USDT_TRC20'].map((a) => (
                                    <button key={a} type="button" onClick={() => set('asset')(a)}
                                        className={`h-9 rounded-md border text-xs font-semibold ${form.asset === a ? 'border-[#d4af37]/70 bg-[#d4af37]/15 text-[#f3e3a6]' : 'border-slate-800 bg-slate-950 text-slate-400'}`}
                                        data-testid={`opportunity-asset-${a}`} aria-pressed={form.asset === a}>{a === 'BTC' ? 'BTC' : 'USDT'}</button>
                                ))}
                            </div></div>
                        <div className="space-y-1"><p className="text-slate-400 text-xs">Fecha objetivo</p>
                            <Input type="date" value={form.target_date} onChange={set('target_date')} className="bg-slate-950 border-slate-800 text-white" data-testid="opportunity-date-input" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1"><p className="text-slate-400 text-xs">Importe mínimo (EUR, opcional)</p>
                            <Input type="number" min="0" step="0.01" value={form.min_amount} onChange={set('min_amount')} placeholder="0,00" className="bg-slate-950 border-slate-800 text-white" data-testid="opportunity-min-input" /></div>
                        <div className="space-y-1"><p className="text-slate-400 text-xs">Estado</p>
                            <select value={form.status} onChange={set('status')} className="w-full h-9 rounded-md bg-slate-950 border border-slate-800 text-white text-sm px-2" data-testid="opportunity-status-select">
                                {Object.entries(OPP_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select></div>
                    </div>
                    <div className="space-y-1"><p className="text-slate-400 text-xs">Descripción (opcional)</p>
                        <Textarea value={form.description || ''} onChange={set('description')} placeholder="Breve descripción visible para los usuarios" className="bg-slate-950 border-slate-800 text-white text-sm min-h-[70px]" data-testid="opportunity-desc-input" /></div>
                    <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                        <input type="checkbox" checked={!!form.published} onChange={(e) => set('published')(e.target.checked)} data-testid="opportunity-published-checkbox" />
                        Publicada (visible para los usuarios)
                    </label>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    <Button onClick={save} disabled={saving} data-testid="opportunity-save-btn">
                        {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />} {initial?.id ? 'Guardar' : 'Publicar'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const OpportunitiesManager = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [busy, setBusy] = useState(null);

    const load = async () => {
        try {
            const { data } = await investmentsAPI.adminOpportunities();
            setItems(data.opportunities || []);
        } catch {
            toast.error('Error al cargar oportunidades');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const togglePublish = async (o) => {
        setBusy(o.id);
        try {
            await investmentsAPI.adminUpdateOpportunity(o.id, { ...o, published: !o.published });
            toast.success(o.published ? 'Oportunidad oculta' : 'Oportunidad publicada');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(null); }
    };
    const archive = async (o) => {
        if (!window.confirm(`¿Archivar «${o.name}»? Dejará de mostrarse a los usuarios.`)) return;
        setBusy(o.id);
        try {
            await investmentsAPI.adminArchiveOpportunity(o.id);
            toast.success('Oportunidad archivada');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(null); }
    };

    return (
        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden" data-testid="opportunities-manager">
            <div className="px-4 py-3 border-b border-slate-800 flex flex-wrap items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#e8d48b]" />
                <p className="text-white font-semibold text-sm flex-1">Oportunidades publicadas <span className="text-slate-500 font-normal">· aparecen en el bloque Investment Opportunities</span></p>
                <Button size="sm" onClick={() => setDialog({ open: true, initial: null })} data-testid="opportunity-new-btn"><Plus className="w-4 h-4 mr-1" /> Nueva oportunidad</Button>
            </div>
            {loading ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-amber-300 animate-spin" /></div>
                : items.length === 0 ? <p className="text-slate-600 text-sm text-center py-8" data-testid="opportunities-empty">Aún no has publicado oportunidades.</p>
                : (
                    <div className="divide-y divide-slate-800/60">
                        {items.map((o) => {
                            const st = OPP_STATUS[o.status] || OPP_STATUS.upcoming;
                            const d = daysUntil(o.target_date);
                            return (
                                <div key={o.id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`admin-opportunity-${o.id}`}>
                                    <div className="flex-1 min-w-[220px]">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="text-white text-sm font-semibold">{o.name}</p>
                                            <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase ${st.cls}`}>{st.label}</span>
                                            {!o.published && <span className="px-2 py-0.5 rounded-md border border-slate-700 text-[10px] text-slate-400 uppercase">Oculta</span>}
                                        </div>
                                        <p className="text-slate-400 text-xs mt-0.5 flex flex-wrap items-center gap-x-2">
                                            <span>{o.asset === 'BTC' ? 'BTC' : 'USDT TRC20'}</span>
                                            <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{fmtTarget(o.target_date)} ({d > 0 ? `en ${d} d` : d === 0 ? 'hoy' : 'alcanzada'})</span>
                                            {o.min_amount ? <span>· desde {fmtEur(o.min_amount)}</span> : null}
                                            <span className="text-[#f3e3a6]">· {o.reservations?.count || 0} reservas · {fmtEur(o.reservations?.total)}</span>
                                        </p>
                                    </div>
                                    <Button size="sm" variant="outline" onClick={() => togglePublish(o)} disabled={busy === o.id} className="h-8" data-testid={`opportunity-toggle-${o.id}`}>
                                        {o.published ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}{o.published ? 'Ocultar' : 'Publicar'}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, initial: o })} className="h-8" data-testid={`opportunity-edit-${o.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                                    <Button size="sm" onClick={() => archive(o)} disabled={busy === o.id} className="h-8 bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25" data-testid={`opportunity-archive-${o.id}`}><Archive className="w-3.5 h-3.5 mr-1" /> Archivar</Button>
                                </div>
                            );
                        })}
                    </div>
                )}
            <OpportunityDialog open={dialog.open} initial={dialog.initial} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />
        </div>
    );
};
